import './styles/MultistepForm.scss';

import {
  Button,
  Divider,
  Grid,
  Step,
  StepButton,
  StepConnector,
  StepLabel,
  Stepper,
  Tooltip
} from '@mui/material';

import { Form, useFormikContext } from 'formik';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { actions as FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { translations } from 'locales/i18n';

import SubmittingLoader from '../Util/SubmittingLoader';
import StepIcon from './components/StepIcon';

interface Props {
  steps: string[];
  children: (
    acticeStep: number,
    setActiveStep: React.Dispatch<React.SetStateAction<number>>
  ) => JSX.Element;
  stepValidator?: (activeStep: number, values: any) => boolean;
  onCancel: Function;
  submitting: boolean;
  hideStepper?: boolean;
  isStepperClickable?: boolean;
  additionalSave?: JSX.Element;
  customSaveText?: string;
  isDisabled?: boolean;
  customInvalidFormTooltip?: string;
}

export default function MultistepForm({
  steps,
  children,
  stepValidator,
  onCancel,
  submitting,
  hideStepper,
  isStepperClickable,
  additionalSave,
  customSaveText,
  isDisabled,
  customInvalidFormTooltip
}: Props): ReactElement {
  const [activeStep, setActiveStep] = useState(0);
  const formik = useFormikContext();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // This scrolls the modal to the top on step change
  useEffect(() => {
    const chatContainer = document.getElementsByClassName('modal__inner__body');
    if (chatContainer?.[0]) {
      chatContainer[0].scroll({ top: 0, behavior: 'smooth' });
    }
  }, [activeStep]);

  return (
    <Form className="multistep-form">
      <Grid container>
        {!hideStepper && (
          <Grid item xs={12} style={{ marginTop: '1em' }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              className="multistep-form__steps"
              connector={<StepConnector className="multistep-form__steps__connector" />}
            >
              {!isStepperClickable
                ? steps.map(label => (
                    <Step key={label}>
                      <StepLabel StepIconComponent={StepIcon}>{label}</StepLabel>
                    </Step>
                  ))
                : steps.map((label, index) => (
                    <Step key={label} disabled={false}>
                      <StepButton
                        disableTouchRipple={true}
                        onClick={() => {
                          setActiveStep(index);
                        }}
                      >
                        <StepLabel StepIconComponent={StepIcon}>{label}</StepLabel>
                      </StepButton>
                    </Step>
                  ))}
            </Stepper>
          </Grid>
        )}

        <Grid
          container
          className="multistep-form__content"
          sx={{ padding: { xs: 'unset', xl: '2em' } }}
        >
          <Grid container className="multistep-form__content__gutter">
            {children(activeStep, setActiveStep)}
          </Grid>
        </Grid>
        <Grid container className="multistep-form__controls">
          <Grid item xs={12} style={{ marginBottom: '1em' }}>
            <Divider />
          </Grid>
          <Grid container justifyContent={'flex-end'}>
            <Grid item xs xl>
              <Grid container justifyContent={'space-between'}>
                <Grid item xs xl>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      dispatch(FormErrorActions.close());
                      onCancel();
                    }}
                    disabled={submitting}
                  >
                    {t(translations.common.cancel())}
                  </Button>
                </Grid>
                {activeStep > 0 && (
                  <Grid container item xs xl justifyContent="flex-end">
                    <Button
                      key="back"
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setActiveStep(activeStep - 1);
                      }}
                      sx={{
                        marginRight: { xs: 'unset', sm: '1em', md: '1em', lg: '1em', xl: '1em' }
                      }}
                    >
                      {t(translations.common.back())}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {activeStep < steps.length - 1 ? (
              <Button
                key="next"
                variant="contained"
                color="primary"
                onClick={() => {
                  setActiveStep(activeStep + 1);
                }}
                disabled={stepValidator && !stepValidator(activeStep, formik.values)}
                style={{ color: 'white' }}
              >
                {t(translations.common.next())}
              </Button>
            ) : (
              <>
                {additionalSave && additionalSave}
                <Grid item alignSelf={'flex-end'}>
                  <Tooltip
                    title={
                      formik.isValid
                        ? ''
                        : customInvalidFormTooltip ?? t(translations.validation.formHasErrors())
                    }
                  >
                    <span
                      style={{
                        position: 'relative'
                      }}
                    >
                      <Button
                        key="submit"
                        variant="contained"
                        color="primary"
                        style={{ color: 'white' }}
                        type="submit"
                        disabled={submitting || !formik.isValid || isDisabled}
                        sx={{
                          marginTop: {
                            xs: '1em',
                            sm: 'unset',
                            md: 'unset',
                            lg: 'unset',
                            xl: 'unset'
                          }
                        }}
                      >
                        {customSaveText ? customSaveText : t(translations.common.save())}
                      </Button>
                      {submitting && <SubmittingLoader />}
                    </span>
                  </Tooltip>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Form>
  );
}
